/**
 * BrainyTots Assessment API Integration Layer
 * Handles communication with the FastAPI backend at bt-development.skillit.in
 */

class BrainyTotsAssessmentAPI {
  constructor() {
    // Allow override for local testing (e.g., via CORS proxy)
    const overrideBase = typeof window !== 'undefined' && window.__BT_API_BASE__ ? window.__BT_API_BASE__ : null;
    this.baseURL = overrideBase || 'https://bt-development.skillit.in';
    this.sessionId = null;
    this.childAgeMonths = null;
    this.usingCorrectedAge = false;
    this.locale = this.getShopifyLocale();
  }

  /**
   * Get Shopify locale, fallback to 'en'
   */
  getShopifyLocale() {
    const shopifyLocale = window.Shopify?.locale || 'en';
    const supportedLocales = ['en', 'hi', 'mr', 'ta', 'te', 'ml', 'bn', 'or', 'as', 'gu'];
    return supportedLocales.includes(shopifyLocale) ? shopifyLocale : 'en';
  }

  /**
   * Start a new assessment session
   */
  async startSession(childName, dob, gestationalWeeks = 40, birthWeightG = null) {
    try {
      const response = await fetch(`${this.baseURL}/session/start`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          locale: this.locale,
          child_name: childName,
          dob: dob,
          gestational_weeks: gestationalWeeks,
          birth_weight_g: birthWeightG
        })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Session start failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const data = await response.json();
      this.sessionId = data.session_id;
      this.childAgeMonths = data.chronological_age_months;
      this.usingCorrectedAge = data.using_corrected_age;

      return data;
    } catch (error) {
      console.error('Failed to start session:', error);
      throw error;
    }
  }

  /**
   * Send query to assistant with SSE streaming
   */
  async queryAssistant(userMessage, answerCode = null, confidence = null, elapsedMs = null) {
    if (!this.sessionId) {
      throw new Error('No active session. Start a session first.');
    }

    try {
      const response = await fetch(`${this.baseURL}/assistant/query`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream, application/json'
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          age_months: this.childAgeMonths,
          corrected: this.usingCorrectedAge,
          locale: this.locale,
          user_message: userMessage,
          answer_code: answerCode,
          confidence_override: confidence,
          elapsed_ms: elapsedMs
        })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Assistant query failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      // Handle SSE response
      return this.handleSSEResponse(response);
    } catch (error) {
      console.error('Failed to query assistant:', error);
      throw error;
    }
  }

  /**
   * Handle Server-Sent Events response
   */
  async handleSSEResponse(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const messages = [];

    return new Promise((resolve, reject) => {
      const processChunk = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            // End of stream - resolve with collected messages
            if (messages.length > 0) {
              resolve(messages);
            } else {
              reject(new Error('SSE stream ended with no data'));
            }
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                messages.push(data);
                
                // If this is a question (has answer_options) or final message, we can resolve
                // to allow the frontend to start displaying messages immediately
                if (data.answer_options && data.answer_options.length > 0) {
                  // Don't resolve yet - collect all messages for the full context
                }
              } catch (e) {
                console.warn('Failed to parse SSE data:', line);
              }
            }
          }

          processChunk();
        }).catch(reject);
      };

      processChunk();
    });
  }

  /**
   * Close the assessment session
   */
  async closeSession() {
    if (!this.sessionId) {
      throw new Error('No active session to close.');
    }

    try {
      const response = await fetch(`${this.baseURL}/session/close`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          session_id: this.sessionId
        })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Session close failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to close session:', error);
      throw error;
    }
  }

  /**
   * Get product recommendations
   */
  async getRecommendations() {
    if (!this.sessionId) {
      throw new Error('No active session. Start a session first.');
    }

    try {
      const response = await fetch(`${this.baseURL}/recs?session_id=${this.sessionId}`, { mode: 'cors', headers: { 'Accept': 'application/json' } });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Recommendations failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to get recommendations:', error);
      throw error;
    }
  }

  /**
   * Generate PDF report
   */
  async generateReport() {
    if (!this.sessionId) {
      throw new Error('No active session.');
    }

    try {
      const response = await fetch(`${this.baseURL}/report/generate`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          locale: this.locale
        })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Report generation failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to generate report:', error);
      throw error;
    }
  }

  /**
   * Check report generation status
   */
  async getReportStatus(reportId) {
    try {
      const response = await fetch(`${this.baseURL}/report/status/${reportId}`, { mode: 'cors', headers: { 'Accept': 'application/json' } });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Report status check failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to check report status:', error);
      throw error;
    }
  }

  /**
   * Get session ID for external use
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * Check if session is active
   */
  hasActiveSession() {
    return !!this.sessionId;
  }

  /**
   * Reset session data
   */
  reset() {
    this.sessionId = null;
    this.childAgeMonths = null;
    this.usingCorrectedAge = false;
  }
}

// Export for use in other scripts
window.BrainyTotsAssessmentAPI = BrainyTotsAssessmentAPI;
